import { css } from '@emotion/react';

import {
  borderRadius,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  opacity,
  space,
} from '../shared/styles';
import { ButtonVariants } from './Button.types';
import topoPattern from './TopoPattern';

export const rootStyles = css`
  position: relative;
  padding: ${space['4']} ${space['10']};
  max-height: ${space['12']};
  border: 1px solid transparent;
  font-weight: ${fontWeights.medium};
  border-radius: ${borderRadius.xxl};
  border-color: ${colors.gray['600']};
  background-color: ${colors.light['100']};
  cursor: pointer;
  font-family: ${fonts.base};
  font-size: ${fontSizes.sm};
  line-height: ${lineHeights.none};
  display: inline-flex;
  align-items: center;
  text-align: center;
  white-space: nowrap;
  justify-content: center;

  &:disabled {
    opacity: ${opacity['50']};
    pointer-events: none;
  }

  &:hover {
    text-decoration: none;
  }
`;

export const styles = {
  primary: css`
    background-color: var(--surface-action);
    color: var(--text-on-action);
    border-color: var(--border-action);

    &:hover {
      background-color: var(--surface-action-hover);
      border-color: var(--border-action-hover);
    }

    &:active {
      background-color: var(--surface-action-hover);
      border-color: var(--border-action-hover);
    }

    &:focus {
      outline: 2px solid var(--color-primary-100);
    }
  `,
  secondary: css`
    background-color: transparent;
    color: var(--text-primary);
    border-color: var(--border-secondary);

    &:hover {
      background-color: var(--surface-action-3-hover);
      color: var(--text-primary);
    }

    &:focus {
      outline: 2px solid var(--color-neutral-700);
    }
  `,
  tertiary: css`
    background-color: ${colors.gray['900']};
    color: ${colors.light['100']};
    border-color: ${colors.gray['900']};

    &:hover {
      background-color: ${colors.dark['900']};
      border-color: ${colors.dark['900']};
    }

    &:active {
      background-color: ${colors.dark['900']};
      border-color: ${colors.dark['900']};
    }

    &:focus {
      outline: 2px solid ${colors.olive['600']};
    }
  `,
  plus: css`
    background-color: var(--color-gold-500);
    color: var(--text-primary);
    border-color: var(--color-gold-500);
    box-shadow: var(--shadow-md);

    &:hover {
      background-color: var(--color-gold-600);
      border-color: var(--color-gold-600);
    }

    &:active {
      background-color: var(--color-gold-600);
      border-color: var(--color-gold-600);
    }

    &:focus {
      outline: 2px solid var(--color-gold-500);
    }
  `,
  simple: css`
    background-color: ${colors.light['100']};
    color: ${colors.dark['900']};
    border-color: ${colors.gray['200']};

    &:hover {
      background-color: ${colors.gray['100']};
    }

    &:active {
      background-color: ${colors.gray['100']};
    }

    &:focus {
      outline: 2px solid ${colors.gray['300']};
    }
  `,
  text: css`
    text-decoration: none;
    border-color: transparent;
    color: var(--text-primary);

    &:hover {
      text-decoration: none;
      background-color: var(--surface-action-3-hover);
      border-color: var(--surface-action-3-hover);
    }

    &:focus {
      outline: 2px solid var(--border-secondary);
    }
  `,
  topo: css`
    background-color: ${colors.gray['900']};
    background-image: url(${topoPattern});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    color: ${colors.light['100']};
    border: none;

    &:hover {
      background-color: ${colors.gray['800']};
    }

    &:active {
      background-color: ${colors.gray['800']};
    }

    &:focus {
      outline: 2px solid ${colors.olive['500']};
    }
  `,

  fullWidth: css`
    width: 100%;
  `,

  sizeXs: css`
    padding-top: ${space['2']};
    padding-bottom: ${space['2']};
    padding-left: ${space['3']};
    padding-right: ${space['3']};
    max-height: ${space['8']};
    font-size: ${fontSizes.xs};
    font-weight: ${fontWeights.bold};
  `,

  sizeSm: css`
    padding-top: ${space['3']};
    padding-bottom: ${space['3']};
  `,

  sizeBase: () => css``,

  sizeMd: css`
    padding-top: ${space['3']};
    padding-bottom: ${space['3']};
    padding-left: ${space['6']};
    padding-right: ${space['6']};
  `,

  sizeLg: css`
    padding-top: ${space['5']};
    padding-bottom: ${space['5']};
    padding-left: ${space['12']};
    padding-right: ${space['12']};
    max-height: ${space['14']};
    font-size: ${fontSizes.base};
  `,

  iconOnlyXs: css`
    height: var(--spacing-8);
    min-width: var(--spacing-8);
    max-height: var(--spacing-8);
    padding: 0;
    border-radius: var(--radius-round);
  `,

  iconOnly: css`
    height: var(--spacing-11);
    min-width: var(--spacing-11);
    max-height: var(--spacing-11);
    padding: 0;
    border-radius: var(--radius-round);
  `,

  iconOnlyLg: css`
    height: var(--spacing-20);
    min-width: var(--spacing-20);
    max-height: var(--spacing-20);
    padding: 0;
    border-radius: var(--radius-round);
  `,

  disabled: css`
    opacity: ${opacity['50']};
    cursor: not-allowed;
  `,

  withIcon: css`
    gap: var(--spacing-2);
  `,
};

export const spinnerStyles = (variant: ButtonVariants) => css`
  position: absolute;
  left: calc(50% - var(--spacing-2));

  ${variant === 'secondary' &&
  `
    fill: var(--text-primary);
  `}

  ${variant === 'plus' &&
  `
    fill: var(--text-primary);
  `}

  ${variant === 'text' &&
  `
    fill: var(--text-primary);
  `}
 
  ${variant === 'simple' &&
  `
    fill: var(--text-primary);
  `}
`;

export const labelStyles = (isVisible: boolean) => () =>
  css`
    visibility: ${isVisible ? 'visible' : 'hidden'};
  `;
